import { router } from "@inertiajs/react"
import { useCallback, useState } from "react"

import { assignMissionPath } from "@/routes"
import type { AssignmentMode } from "@/types/mission-assign"

/**
 * OKS-30 — URL state mutator for `Brand/Missions/Assign`.
 *
 * Mirrors the OKS-25 `useCrewListUrl` pattern: state lives in the URL
 * (mode + selection), and Inertia partial-reload `only: ['preview']`
 * refreshes the preview prop without re-rendering the rest of the page.
 *
 * **Wire-format (locked w/ BE — see `mission_assignments_controller.rb`
 * + QA Plan §Contract Tests):**
 *   - URL keys: `mode`, `segment_id`, `crew_membership_ids[]`
 *   - Form param keys: same (top-level — NEVER nested under
 *     `assignment[…]`)
 *   - Default state (no mode selected) emits an empty query string
 *
 * **Mode-switch behaviour:** `setMode(next)` clears the per-mode
 * selection slot (`segment_id` cleared when leaving segment;
 * `crew_membership_ids[]` cleared when leaving handpick). The "switch
 * mode? you'll lose your selection" confirm dialog is owned by the
 * page component — the hook just emits the URL change.
 */
// Keys re-fetched on every URL state change. `selected_mode`,
// `available_memberships`, and `mode_options` MUST be in this list:
// without them, switching mode (esp. via the mode-switch confirm dialog
// from handpick → crew) leaves the FE rendering the old mode's shell
// with the new mode's preview ("dual mode" desync) until the next full
// nav. Adding them is safe for the other partial-reload paths
// (search debounce, segment selection inside a mode) — those don't
// flip mode and the re-served props match what's already rendered.
const RELOAD_KEYS = [
  "preview",
  "applied_segment",
  "applied_handpick_ids",
  "available_memberships",
  "selected_mode",
  "mode_options",
  "can_confirm",
] as const

export interface AssignmentUrlState {
  mode: AssignmentMode | null
  segmentId: number | null
  handpickIds: number[]
}

interface UseAssignmentUrlReturn {
  /** Set mode and clear stale selection slots. */
  setMode: (next: AssignmentMode) => void
  /** Pick a segment (only meaningful when mode === "segment"). */
  setSegment: (id: number | null) => void
  /** Toggle a single membership in the handpick selection. */
  toggleHandpickId: (id: number) => void
  /** Replace the whole handpick selection (e.g. clear-all CTA). */
  setHandpickIds: (ids: number[]) => void
  /** Drop the per-mode selection but stay on the same mode. */
  clearSelection: () => void
  /**
   * OKS-1042 — pick the creator the enrol control is about, and fetch that
   * creator's offer.
   *
   * ⚠️ The id lives in the URL, not in component state, for the same reason the
   * handpick selection does: `enrol_offer` is computed PER CREATOR (its recency
   * is), so a refresh or a back-button with the id only in React state would
   * render one creator's products beside another creator's warnings.
   *
   * ⚠️ Reloads `enrol_offer` ONLY. The batch-preview keys are untouched — this
   * mode has no batch preview, and pulling them would recompute a resolver
   * result nothing on screen consumes.
   */
  setOnBehalfMembership: (id: number | null) => void
  /**
   * True while an Inertia partial reload triggered by this hook is in
   * flight. The preview component reads this to render skeleton state
   * (UX Spec §3 loading row).
   */
  pending: boolean
}

/**
 * Build the URL params for the assign page.
 *
 * Exported for direct unit testing — the Vitest spec asserts the
 * normalization rules (mode survives, empty selections drop, etc.).
 */
export function buildAssignmentParams(
  state: AssignmentUrlState,
): Record<string, string | number | string[] | number[]> {
  const params: Record<string, string | number | string[] | number[]> = {}

  if (state.mode) {
    params.mode = state.mode
    if (state.mode === "segment" && typeof state.segmentId === "number") {
      params.segment_id = state.segmentId
    }
    if (state.mode === "handpick" && state.handpickIds.length > 0) {
      params.crew_membership_ids = state.handpickIds
    }
  }

  return params
}

export function useAssignmentUrl(
  missionId: number,
  current: AssignmentUrlState,
): UseAssignmentUrlReturn {
  const [pending, setPending] = useState(false)

  const visit = useCallback(
    (next: AssignmentUrlState) => {
      const params = buildAssignmentParams(next)
      router.get(assignMissionPath(missionId), params, {
        preserveState: true,
        preserveScroll: true,
        only: [...RELOAD_KEYS],
        onStart: () => setPending(true),
        onFinish: () => setPending(false),
      })
    },
    [missionId],
  )

  const setMode = useCallback(
    (next: AssignmentMode) => {
      visit({
        mode: next,
        segmentId: next === "segment" ? current.segmentId : null,
        handpickIds: next === "handpick" ? current.handpickIds : [],
      })
    },
    [visit, current.segmentId, current.handpickIds],
  )

  const setSegment = useCallback(
    (id: number | null) => {
      visit({ ...current, mode: "segment", segmentId: id, handpickIds: [] })
    },
    [visit, current],
  )

  const toggleHandpickId = useCallback(
    (id: number) => {
      const has = current.handpickIds.includes(id)
      const next = has
        ? current.handpickIds.filter((x) => x !== id)
        : [...current.handpickIds, id]

      visit({
        ...current,
        mode: "handpick",
        segmentId: null,
        handpickIds: next,
      })
    },
    [visit, current],
  )

  const setHandpickIds = useCallback(
    (ids: number[]) => {
      visit({ ...current, mode: "handpick", segmentId: null, handpickIds: ids })
    },
    [visit, current],
  )

  const clearSelection = useCallback(() => {
    visit({ ...current, segmentId: null, handpickIds: [] })
  }, [visit, current])

  const setOnBehalfMembership = useCallback(
    (id: number | null) => {
      router.get(
        assignMissionPath(missionId),
        {
          mode: "on_behalf",
          ...(id === null ? {} : { on_behalf_membership_id: id }),
        },
        {
          preserveState: true,
          preserveScroll: true,
          only: ["enrol_offer"],
          onStart: () => setPending(true),
          onFinish: () => setPending(false),
        },
      )
    },
    [missionId],
  )

  return {
    setMode,
    setSegment,
    toggleHandpickId,
    setHandpickIds,
    clearSelection,
    setOnBehalfMembership,
    pending,
  }
}
